import { useEffect, useRef } from 'react';

import { Icon, IconButton } from '@vestigas/ui';

import { useUniversalFiltersSelector } from '../../hooks/useUniversalFiltersSelector';
import {
  type FilterClause,
  type FilterDraftValue,
  type UniversalDrawerFieldConfig,
  type UniversalFilterClauseValues,
} from '../../types';

import { FiltersDrawerFieldControl } from './FiltersDrawerFieldControl';

type FiltersDrawerConfiguredRowProps = {
  readonly fieldConfig: UniversalDrawerFieldConfig;
  readonly onEdit: (clause: FilterClause) => void;
  readonly onRemove: () => void;
  readonly removeAriaLabel: string;
  readonly shouldAutoFocus: boolean;
  readonly values: UniversalFilterClauseValues;
};

/**
 * The `YYYY-MM-DD` value of the clause a date field is bounded by. Read from
 * the draft so the bound follows the partner field's edits.
 */
function useDateBound(field: string | undefined) {
  return useUniversalFiltersSelector((context) => {
    const value = context.draft.find((clause) => clause.field === field)?.value;

    return typeof value === 'string' ? value : undefined;
  });
}

export function FiltersDrawerConfiguredRow({
  fieldConfig,
  values,
  onEdit,
  onRemove,
  removeAriaLabel,
  shouldAutoFocus,
}: FiltersDrawerConfiguredRowProps) {
  const controlContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!shouldAutoFocus) return;

    // Defer one tick so focus runs after the add-field MUI Menu has finished
    // closing — otherwise its open-state focus management steals focus back.
    // We intentionally do not clear the "pending focus" flag in the parent
    // after focusing: a state clear would trigger a parent re-render, which
    // remounts the react-select internals (see Select.tsx Control memo) and
    // throws focus away. The flag is naturally replaced by the next add.
    const timeoutId = window.setTimeout(() => {
      controlContainerRef.current
        ?.querySelector<HTMLElement>(
          'input, textarea, [role="combobox"], button:not([aria-haspopup])',
        )
        ?.focus();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [shouldAutoFocus]);

  const value = values[fieldConfig.field];
  const dateFieldConfig =
    fieldConfig.input === 'date' ? fieldConfig : undefined;
  const maxDate = useDateBound(dateFieldConfig?.maxDateField);
  const minDate = useDateBound(dateFieldConfig?.minDateField);
  function handleValueChange(next?: FilterDraftValue) {
    onEdit({ field: fieldConfig.field, value: next });
  }

  const control =
    fieldConfig.input === 'custom' && fieldConfig.fields ? (
      fieldConfig.render({ onChange: onEdit, values })
    ) : fieldConfig.input === 'custom' ? (
      fieldConfig.render({
        field: fieldConfig.field,
        label: fieldConfig.label,
        onChange: handleValueChange,
        value,
      })
    ) : (
      <FiltersDrawerFieldControl
        fieldConfig={fieldConfig}
        maxDate={maxDate}
        minDate={minDate}
        value={value}
        onChange={handleValueChange}
      />
    );

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`universal-filters-drawer-row-${fieldConfig.field}`}
    >
      <span className="text-tiga-text text-sm font-medium">
        {fieldConfig.label}
      </span>
      <div className="flex items-start gap-2">
        <div ref={controlContainerRef} className="min-w-0 flex-1">
          {control}
        </div>
        <IconButton
          aria-label={removeAriaLabel}
          className="mt-1.5 shrink-0"
          size="sm"
          type="button"
          onClick={onRemove}
        >
          <Icon metaphor="clear" />
        </IconButton>
      </div>
    </div>
  );
}
